import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  getAllCustomerOffersApi,
  getArchivedCustomerOffersApi,
  downloadOfferPdfApi,
  sendOfferAgreementEmail,
  archiveCustomerOfferApi,
  unarchiveCustomerOfferApi,
  CustomerOfferListItem,
  OFFER_STATUS_DRAFT,
  OFFER_STATUS_ACCEPTED,
  OFFER_STATUS_EXPIRED,
  OFFER_STATUS_DECLINED,
  OFFER_STATUS_CANCELLED,
  getPreferredOfferLanguageApi,
} from '../features/customer-offer/customer-offer.service';
import { fetchPersonalData, fetchUserPreferences } from '../features/profile-settings/profile-settings.slice';
import { resolveOfferLang, OFFER_LANG_STORAGE_KEY } from '../features/customer-offer/customer-offer-language.util';
import { formatOfferDate } from '../features/customer-offer/customer-offer-date.util';
import { ContractLanguageDropdown } from './contract-language-dropdown.component';
import EmailTrackingModal from './dashboard/email-tracking-modal.component';
import { DocumentOutcome } from '../features/email-tracking/email-tracking.types';
import { TrackingIcon } from '../assets/dashboard-icon.svg';
import './customer-contracts-grid.component.scss';

const OFFER_STATUS: Record<number, { label: string; cls: string }> = {
  0: { label: 'COG_STATUS_DRAFT',             cls: 'draft' },
  1: { label: 'COG_STATUS_PENDING_SIGNATURE', cls: 'pending-signature' },
  2: { label: 'COG_STATUS_ACCEPTED',          cls: 'active' },
  3: { label: 'COG_STATUS_EXPIRED',           cls: 'expired' },
  4: { label: 'COG_STATUS_DECLINED',          cls: 'cancelled' },
  5: { label: 'COG_STATUS_CANCELLED',         cls: 'cancelled' },
};

function offerDocumentOutcome(offerStatus?: number): DocumentOutcome {
  switch (offerStatus) {
    case OFFER_STATUS_ACCEPTED: return 'signed';
    case OFFER_STATUS_DECLINED: return 'declined';
    case OFFER_STATUS_CANCELLED: return 'cancelled';
    case OFFER_STATUS_EXPIRED: return 'expired';
    default: return 'pending';
  }
}

const PAGE_SIZE = 10;

const formatDate = formatOfferDate;

function formatAmount(amount: number, currency: string): string {
  return `${currency} ${amount.toLocaleString()}`;
}

// Client avatar helpers — mirror the platform data table (My Invoices).
function getClientInitial(name?: string): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts.slice(0, 2).map((p) => p.charAt(0)).join('').toUpperCase();
}

function getAvatarVariant(name?: string): number {
  const key = (name || '').trim();
  let sum = 0;
  for (let i = 0; i < key.length; i++) sum += key.charCodeAt(i);
  return (sum % 5) + 1;
}

function CalendarIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="3" y="4.5" width="18" height="17" rx="2.5" stroke="currentColor" strokeWidth="2" />
      <path d="M3 9h18M8 2.5v4M16 2.5v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 1v9M5 7l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M2 11v2a1 1 0 001 1h10a1 1 0 001-1v-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function EditIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M11.5 2.5a1.414 1.414 0 012 2L5 13H3v-2L11.5 2.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function SendIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M14.5 1.5L7.5 8.5M14.5 1.5L10 14.5L7.5 8.5M14.5 1.5L1.5 6L7.5 8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function DuplicateIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M5.5 5.5V3a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1h-2.5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M3.5 5.5h6a1 1 0 011 1v6a1 1 0 01-1 1h-6a1 1 0 01-1-1v-6a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    </svg>
  );
}

function ArchiveIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2 3a1 1 0 011-1h10a1 1 0 011 1v2H2V3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M2.5 5.5V12a1 1 0 001 1h9a1 1 0 001-1V5.5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M6 8h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function UnarchiveIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2 3a1 1 0 011-1h10a1 1 0 011 1v2H2V3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M2.5 5.5V12a1 1 0 001 1h9a1 1 0 001-1V5.5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M6 9.5L8 7.5L10 9.5M8 7.5V11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

export const CustomerOffersGrid = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const userId = useSelector((state: any) => state.auth?.user?.userId);
  const personalData = useSelector((state: any) => state.profileSettings?.personalData);
  const userPreferences = useSelector((state: any) => state.profileSettings?.userPreferences);
  const [activeTab, setActiveTab] = useState<'all' | 'archived'>('all');
  const [offers, setOffers] = useState<CustomerOfferListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [sendingId, setSendingId] = useState<number | null>(null);
  const [archivingId, setArchivingId] = useState<number | null>(null);
  const [trackingOffer, setTrackingOffer] = useState<CustomerOfferListItem | null>(null);
  const [page, setPage] = useState(1);
  const language = new URLSearchParams(window.location.search).get('lang')

  // ── Sliding pill-tabs indicator ──────────────────────────────────────────────
  const tabsRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });
  const [tabsReady, setTabsReady] = useState(false);

  function measureIndicator() {
    const el = tabsRef.current?.querySelector<HTMLButtonElement>('.ccg-pill-tabs__tab.is-active');
    if (el) setIndicator({ left: el.offsetLeft, width: el.offsetWidth });
  }

  // Position before paint so there's no flash / slide-in on first mount.
  useLayoutEffect(() => {
    measureIndicator();
  }, [activeTab]);

  useEffect(() => {
    window.addEventListener('resize', measureIndicator);
    return () => window.removeEventListener('resize', measureIndicator);
  }, []);

  // Enable the slide transition only after the first frame, so the pill animates
  // on real tab switches instead of sliding in from the left on mount.
  useEffect(() => {
    const raf = requestAnimationFrame(() => setTabsReady(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // ── Action tooltip ───────────────────────────────────────────────────────────
  const tipRef = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<{ text: string; x: number; y: number } | null>(null);
  const [tipShift, setTipShift] = useState(0);
  const showTip = (el: HTMLElement, text: string) => {
    const r = el.getBoundingClientRect();
    setTipShift(0);
    setTip({ text, x: r.left + r.width / 2, y: r.top });
  };
  const hideTip = () => setTip(null);
  const tipHandlers = (text: string) => ({
    onMouseEnter: (e: React.MouseEvent<HTMLElement>) => showTip(e.currentTarget, text),
    onMouseLeave: hideTip,
    onFocus: (e: React.FocusEvent<HTMLElement>) => showTip(e.currentTarget, text),
    onBlur: hideTip,
  });

  useLayoutEffect(() => {
    if (!tip || !tipRef.current) return;
    const margin = 8;
    const rect = tipRef.current.getBoundingClientRect();
    let delta = 0;
    if (rect.left < margin) delta = margin - rect.left;
    else if (rect.right > window.innerWidth - margin) delta = window.innerWidth - margin - rect.right;
    if (delta !== 0) setTipShift((s) => s + delta);
  }, [tip]);

  function loadOffers(tab: 'all' | 'archived') {
    setLoading(true);
    setError(false);
    setPage(1);
    const apiCall = tab === 'archived' ? getArchivedCustomerOffersApi : getAllCustomerOffersApi;
    apiCall()
      .then(res => setOffers(res.response || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadOffers(activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (userId) dispatch(fetchPersonalData(userId));
    dispatch(fetchUserPreferences());
  }, [dispatch, userId]);

  const [lang, setLang] = useState(resolveOfferLang);

  // Scoped to offer actions (PDF locale, sent-email locale) only — does
  // not touch the platform-wide UI language.
  const handleOfferLangChange = (newLang: string) => {
    setLang(newLang);
    window.localStorage.setItem(OFFER_LANG_STORAGE_KEY, newLang);
  };

  useEffect(() => {
    if (window.localStorage.getItem(OFFER_LANG_STORAGE_KEY)) return;

    getPreferredOfferLanguageApi(lang)
      .then(res => {
        const preferred = res.response;
        if (preferred && preferred !== lang) handleOfferLangChange(preferred);
      })
      .catch(() => { /* keep the locally-resolved default */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleDownloadPdf(offer: CustomerOfferListItem) {
    setDownloadingId(offer.id);
    try {
      const blob = await downloadOfferPdfApi(offer.id, lang);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `offer-${offer.offerNumber}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t('COG_DOWNLOAD_SUCCESS') as string);
    } catch {
      toast.error(t('COG_DOWNLOAD_ERROR') as string);
    } finally {
      setDownloadingId(null);
    }
  }

  function handleEdit(offer: CustomerOfferListItem) {
    if (offer.isArchived) return;
    navigate(`/v2/create-offer?id=${offer.id}&lang=${language}`);
  }

  async function handleSendEmail(offer: CustomerOfferListItem) {
    if (
      offer.isArchived ||
      offer.offerStatus === OFFER_STATUS_EXPIRED ||
      offer.offerStatus === OFFER_STATUS_ACCEPTED ||
      offer.offerStatus === OFFER_STATUS_DECLINED ||
      offer.offerStatus === OFFER_STATUS_CANCELLED
    ) return;
    if (!offer.customer?.email) {
      toast.error(t('COG_SEND_NO_EMAIL') as string);
      return;
    }

    setSendingId(offer.id);
    try {
      const senderName =
        personalData?.fullName?.trim() ||
        `${personalData?.firstName ?? ''} ${personalData?.lastName ?? ''}`.trim() ||
        userPreferences?.organizationName ||
        '';

      await sendOfferAgreementEmail(
        offer.id,
        {
          customerName: offer.customer.name,
          customerEmail: offer.customer.email,
          offerNumber: offer.offerNumber,
          offeredServices: offer.offeredServices,
          startDate: offer.startDate,
          endDate: offer.endDate,
          noEndDate: offer.noEndDate,
          amount: offer.amount,
          currency: offer.currency,
          compensationType: offer.compensationType,
        },
        senderName,
        lang,
        offer.offerStatus
      );
      loadOffers('all')

      toast.success(t('COG_SEND_SUCCESS') as string);
    } catch {
      toast.error(t('COG_SEND_ERROR') as string);
    } finally {
      setSendingId(null);
    }
  }

  function handleDuplicate(offer: CustomerOfferListItem) {
    navigate(`/v2/create-offer?duplicateId=${offer.id}&lang=${language}`);
  }

  async function handleToggleArchive(offer: CustomerOfferListItem) {
    const willArchive = !offer.isArchived;
    setArchivingId(offer.id);
    try {
      if (willArchive) {
        await archiveCustomerOfferApi(offer.id);
      } else {
        await unarchiveCustomerOfferApi(offer.id);
      }
      loadOffers(activeTab);
      toast.success(t(willArchive ? 'COG_ARCHIVE_SUCCESS' : 'COG_UNARCHIVE_SUCCESS') as string);
    } catch {
      toast.error(t(willArchive ? 'COG_ARCHIVE_ERROR' : 'COG_UNARCHIVE_ERROR') as string);
    } finally {
      setArchivingId(null);
    }
  }

  const totalPages = Math.max(1, Math.ceil(offers.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = offers.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="ccg">
      <div className="ccg__header">
        <h2 className="ccg__title">{t('COG_TITLE')}</h2>
        <div className="ccg__header-actions" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <ContractLanguageDropdown value={lang} onChange={handleOfferLangChange} />
          <button
            className="ccg__create-btn"
            type="button"
            onClick={() => navigate(`/v2/create-offer?lang=${lang}`)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <PlusIcon /> {t('COG_BTN_NEW')}
          </button>
        </div>
      </div>

      <div className="ccg__tabs">
        <div className="ccg-pill-tabs" role="tablist" ref={tabsRef} aria-label={t('COG_TITLE') as string}>
          <span
            className={`ccg-pill-tabs__indicator${tabsReady ? ' is-animated' : ''}`}
            aria-hidden="true"
            style={{ transform: `translateX(${indicator.left}px)`, width: indicator.width }}
          />
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'all'}
            className={`ccg-pill-tabs__tab${activeTab === 'all' ? ' is-active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            {t('COG_TAB_ALL')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'archived'}
            className={`ccg-pill-tabs__tab${activeTab === 'archived' ? ' is-active' : ''}`}
            onClick={() => setActiveTab('archived')}
          >
            {t('COG_TAB_ARCHIVED')}
          </button>
        </div>
      </div>

      {loading && (
        <div className="ccg__skeleton" aria-hidden="true">
          <div className="ccg__skel-head" />
          {Array.from({ length: 7 }).map((_, i) => (
            <div className="ccg__skel-row" key={i}>
              {Array.from({ length: 8 }).map((__, j) => (
                <div className="ccg__skel-cell" key={j} />
              ))}
            </div>
          ))}
        </div>
      )}

      {!loading && error && (
        <div className="ccg__state ccg__state--error">
          <p className="ccg__state-text">{t('COG_ERROR')}</p>
        </div>
      )}

      {!loading && !error && offers.length === 0 && (
        <div className="ccg__state ccg__state--empty">
          <span className="ccg__empty-icon" aria-hidden="true">
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 12h-6l-2 3h-4l-2-3H2" />
              <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
            </svg>
          </span>
          <p className="ccg__state-text">{t(activeTab === 'archived' ? 'COG_EMPTY_ARCHIVED' : 'COG_EMPTY')}</p>
          {activeTab === 'all' && (
            <button
              className="ccg__create-btn"
              onClick={() => navigate(`/v2/create-offer?lang=${lang}`)}
            >
              {t('COG_CREATE_FIRST')}
            </button>
          )}
        </div>
      )}

      {!loading && !error && offers.length > 0 && (
        <>
        <div className="ccg__table-wrap">
          <table className="ccg__table">
            <thead>
              <tr className="ccg__thead-row">
                <th className="ccg__th">{t('COG_COL_OFFER_NO')}</th>
                <th className="ccg__th">{t('COG_COL_CUSTOMER')}</th>
                <th className="ccg__th">{t('COG_COL_SERVICE')}</th>
                <th className="ccg__th">{t('COG_COL_START_DATE')}</th>
                <th className="ccg__th">{t('COG_COL_END_DATE')}</th>
                <th className="ccg__th">{t('COG_COL_AMOUNT')}</th>
                <th className="ccg__th">{t('COG_COL_STATUS')}</th>
                <th className="ccg__th">{t('COG_COL_CREATED')}</th>
                <th className="ccg__th">{t('COG_COL_ACTIONS')}</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map(offer => {
                const status = OFFER_STATUS[offer.offerStatus] ?? {
                  label: 'COG_STATUS_DRAFT',
                  cls: 'draft',
                };
                const isDownloading = downloadingId === offer.id;
                const editTip = offer.isArchived
                  ? t('COG_ACTION_EDIT_DISABLED_ARCHIVED')
                  : offer.offerStatus === OFFER_STATUS_DRAFT
                    ? t('COG_ACTION_EDIT')
                    : t('COG_ACTION_EDIT_DISABLED');
                const sendTip = offer.isArchived
                  ? t('COG_ACTION_SEND_DISABLED_ARCHIVED')
                  : offer.offerStatus === OFFER_STATUS_EXPIRED
                    ? t('COG_ACTION_SEND_DISABLED_EXPIRED')
                    : offer.offerStatus === OFFER_STATUS_ACCEPTED
                      ? t('COG_ACTION_SEND_DISABLED_ACCEPTED')
                      : offer.offerStatus === OFFER_STATUS_DECLINED
                        ? t('COG_ACTION_SEND_DISABLED_DECLINED')
                        : offer.offerStatus === OFFER_STATUS_CANCELLED
                          ? t('COG_ACTION_SEND_DISABLED_CANCELLED')
                          : t('COG_ACTION_SEND');
                const archiveTip = t(offer.isArchived ? 'COG_ACTION_UNARCHIVE' : 'COG_ACTION_ARCHIVE');
                const downloadTip = t('COG_ACTION_DOWNLOAD_PDF');
                const duplicateTip = t('COG_ACTION_DUPLICATE');
                const trackingTip = t('COG_TRACKING_TITLE');
                return (
                  <tr key={offer.id} className="ccg__tr">
                    <td className="ccg__td ccg__td--contract-no">
                      {offer.offerNumber}
                    </td>
                    <td className="ccg__td">
                      <span className="ccg__client">
                        <span className={`ccg__avatar ccg__avatar--v${getAvatarVariant(offer.customer?.name)}`}>
                          {getClientInitial(offer.customer?.name)}
                        </span>
                        <span className="ccg__client-info">
                          <span className="ccg__customer-name">{offer.customer?.name ?? '—'}</span>
                          <span className="ccg__customer-email">{offer.customer?.email ?? ''}</span>
                        </span>
                      </span>
                    </td>
                    <td className="ccg__td">{offer.offeredServices ?? '—'}</td>
                    <td className="ccg__td">
                      <span className="ccg__date"><CalendarIcon /> {formatDate(offer.startDate)}</span>
                    </td>
                    <td className="ccg__td">
                      {offer.noEndDate ? (
                        <span className="ccg__no-end">{t('COG_NO_END_DATE')}</span>
                      ) : (
                        <span className="ccg__date"><CalendarIcon /> {formatDate(offer.endDate)}</span>
                      )}
                    </td>
                    <td className="ccg__td ccg__td--amount">
                      <span className="ccg__amount-pill">{formatAmount(offer.amount, offer.currency)}</span>
                    </td>
                    <td className="ccg__td">
                      <span className={`ccg__badge ccg__badge--${status.cls}`}>
                        {t(status.label)}
                      </span>
                    </td>
                    <td className="ccg__td ccg__td--muted">{formatDate(offer.createdDate)}</td>
                    <td className="ccg__td">
                      <div className="ccg__actions">
                        <span className="ccg__action-wrap" {...tipHandlers(downloadTip as string)}>
                          <button
                            className="ccg__action-btn ccg__action-btn--download"
                            onClick={() => handleDownloadPdf(offer)}
                            disabled={isDownloading}
                            aria-label={downloadTip as string}
                          >
                            {isDownloading ? (
                              <span className="ccg__action-spinner" />
                            ) : (
                              <DownloadIcon />
                            )}
                          </button>
                        </span>
                        <span className="ccg__action-wrap" {...tipHandlers(editTip as string)}>
                          <button
                            className="ccg__action-btn ccg__action-btn--edit"
                            onClick={() => handleEdit(offer)}
                            disabled={offer.isArchived || offer.offerStatus !== OFFER_STATUS_DRAFT}
                            aria-label={editTip as string}
                          >
                            <EditIcon />
                          </button>
                        </span>
                        <span className="ccg__action-wrap" {...tipHandlers(sendTip as string)}>
                          <button
                            className="ccg__action-btn ccg__action-btn--send"
                            onClick={() => handleSendEmail(offer)}
                            disabled={
                              offer.isArchived ||
                              offer.offerStatus === OFFER_STATUS_EXPIRED ||
                              offer.offerStatus === OFFER_STATUS_ACCEPTED ||
                              offer.offerStatus === OFFER_STATUS_DECLINED ||
                              offer.offerStatus === OFFER_STATUS_CANCELLED ||
                              sendingId === offer.id
                            }
                            aria-label={sendTip as string}
                          >
                            {sendingId === offer.id ? (
                              <span className="ccg__action-spinner" />
                            ) : (
                              <SendIcon />
                            )}
                          </button>
                        </span>
                        <span className="ccg__action-wrap" {...tipHandlers(trackingTip as string)}>
                          <button
                            className="ccg__action-btn ccg__action-btn--tracking"
                            onClick={() => setTrackingOffer(offer)}
                            aria-label={trackingTip as string}
                          >
                            <TrackingIcon />
                          </button>
                        </span>
                        {(offer.offerStatus === OFFER_STATUS_EXPIRED || offer.offerStatus === OFFER_STATUS_DECLINED) && (
                          <span className="ccg__action-wrap" {...tipHandlers(duplicateTip as string)}>
                            <button
                              className="ccg__action-btn ccg__action-btn--duplicate"
                              onClick={() => handleDuplicate(offer)}
                              aria-label={duplicateTip as string}
                            >
                              <DuplicateIcon />
                            </button>
                          </span>
                        )}
                        <span className="ccg__action-wrap" {...tipHandlers(archiveTip as string)}>
                          <button
                            className="ccg__action-btn ccg__action-btn--archive"
                            onClick={() => handleToggleArchive(offer)}
                            disabled={archivingId === offer.id}
                            aria-label={archiveTip as string}
                          >
                            {archivingId === offer.id ? (
                              <span className="ccg__action-spinner" />
                            ) : offer.isArchived ? (
                              <UnarchiveIcon />
                            ) : (
                              <ArchiveIcon />
                            )}
                          </button>
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="ccg__pagination">
            <div className="ccg__pager">
              <button
                className="ccg__page-btn ccg__page-btn--nav"
                disabled={currentPage === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label={t('PREV', { defaultValue: 'Previous' }) as string}
              >
                <ChevronLeftIcon />
                {t('PREV', { defaultValue: 'Prev' })}
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  className={`ccg__page-btn ccg__page-btn--num${p === currentPage ? ' ccg__page-btn--active' : ''}`}
                  onClick={() => setPage(p)}
                  aria-current={p === currentPage ? 'page' : undefined}
                >
                  {p}
                </button>
              ))}
              <button
                className="ccg__page-btn ccg__page-btn--nav"
                disabled={currentPage === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                aria-label={t('NEXT', { defaultValue: 'Next' }) as string}
              >
                {t('NEXT', { defaultValue: 'Next' })}
                <ChevronRightIcon />
              </button>
            </div>
          </div>
        )}
        </>
      )}

      <EmailTrackingModal
        show={trackingOffer !== null}
        variant="document"
        kind="customer-offer"
        invoiceId={trackingOffer?.id ?? null}
        documentOutcome={offerDocumentOutcome(trackingOffer?.offerStatus)}
        documentSentDate={trackingOffer?.sentDate}
        documentNumber={trackingOffer?.offerNumber}
        titleKey="COG_TRACKING_TITLE"
        onClose={() => setTrackingOffer(null)}
      />

      {tip && createPortal(
        <div
          ref={tipRef}
          className="ccg-tip"
          style={{
            left: tip.x,
            top: tip.y,
            transform: `translate(calc(-50% + ${tipShift}px), calc(-100% - 8px))`,
            ['--tip-arrow' as string]: `${-tipShift}px`,
          } as React.CSSProperties}
        >
          {tip.text}
        </div>,
        document.body,
      )}
    </div>
  );
};
